
export default function Features() {
  const features = [
    {
      icon: 'ri-eye-line',
      title: '광학 센싱 기술',
      description: '첨단 광학 센서를 활용한 비접촉식 수량 측정으로 자석 조작이 불가능하며, 99.9% 정확도의 정밀 계량을 실현합니다.',
      gradient: 'from-teal-600 to-cyan-600',
      stats: { value: '99.9%', label: '측정 정확도' }
    },
    {
      icon: 'ri-wifi-line',
      title: 'IoT 실시간 연동',
      description: '사물인터넷 기술로 실시간 데이터 전송 및 모니터링이 가능하며, 스마트폰으로 언제 어디서나 확인할 수 있습니다.',
      gradient: 'from-cyan-600 to-teal-600',
      stats: { value: '24/7', label: '실시간 모니터링' }
    },
    {
      icon: 'ri-shield-check-line',
      title: '부정사용 방지',
      description: '자석 조작 등 부정사용을 원천 차단하는 광학 센싱 보안 시스템으로 공정한 계량과 수익 보호를 보장합니다.',
      gradient: 'from-teal-700 to-teal-500',
      stats: { value: '100%', label: '부정사용 차단' }
    },
    {
      icon: 'ri-flask-line',
      title: '스테인리스 재질',
      description: '의료용 등급의 스테인리스 재질을 사용하여 납 성분을 완전히 제거하고, 일본보다 경쟁력 있는 가격을 실현했습니다.',
      gradient: 'from-gray-600 to-gray-800',
      stats: { value: '0%', label: '납 성분' }
    },
    {
      icon: 'ri-cloud-line',
      title: '클라우드 데이터 관리',
      description: '모든 정보는 클라우드 시스템에 저장되며 에너지 절약(Saving)에 활용되어 환경을 보호하고 탄소 제로 사회를 실현합니다.',
      gradient: 'from-teal-600 to-emerald-600',
      stats: { value: '실시간', label: '데이터 분석' }
    },
    {
      icon: 'ri-leaf-line',
      title: '에너지 효율 · 탄소 제로',
      description: '저전력 설계로 장기간 배터리 사용이 가능하며, 탄소 제로(Carbon-free) 비전을 실현하는 친환경 솔루션입니다.',
      gradient: 'from-emerald-600 to-teal-600',
      stats: { value: '5년+', label: '배터리 수명' }
    }
  ];

  return (
    <section id="features" className="py-16 md:py-24 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-teal-600 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-teal-600 to-transparent"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-16">
          <div className="inline-block px-5 py-2 bg-teal-50 border border-teal-200 rounded-full mb-4">
            <span className="text-teal-600 text-xs md:text-sm font-bold uppercase tracking-wider">Product Features</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 mb-4 md:mb-6">
            제품 <span className="text-teal-600">주요 특징</span>
          </h2>
          <p className="text-sm md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed font-medium">
            광학 센싱 기술 기반의 차세대 스마트 수도미터기
          </p>
        </div>

        {/* Product Highlight */}
        <div className="mb-10 md:mb-16 bg-gradient-to-br from-[#0a3d4f] to-[#0a2e3d] rounded-2xl md:rounded-3xl p-5 md:p-8 lg:p-12 shadow-2xl">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="flex justify-center">
              <div className="relative w-[200px] h-[200px] sm:w-[260px] sm:h-[260px] md:w-[340px] md:h-[340px] lg:w-[380px] lg:h-[380px]">
                <div className="absolute inset-0 bg-teal-400/20 rounded-full blur-[60px]"></div>
                <img
                  src="https://static.readdy.ai/image/2299103fa86076f2ed06076e2d6cbff8/3bb60e1413457df40dbc6ee39e0447f8.png"
                  alt="광센서 스마트 디지털 수도미터"
                  title="로보에셋그리드 광학센싱 스마트 디지털 수도미터기 제품 상세"
                  className="relative w-full h-full object-contain drop-shadow-2xl"
                />
              </div>
            </div>
            <div className="text-white space-y-4 md:space-y-6">
              <h3 className="text-xl md:text-3xl font-black text-center lg:text-left">
                광센서 스마트 디지털 수도미터
              </h3>
              <p className="text-sm md:text-base text-teal-200 leading-relaxed text-center lg:text-left font-medium">
                3C(Carbon-free, Connected, Consumer) 핵심 비전을 기반으로 개발된 차세대 수도미터기입니다. 광학 센싱 기술로 자석 조작을 원천 차단하고, IoT 연동으로 실시간 모니터링이 가능합니다.
              </p>
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <div className="bg-white/10 rounded-xl p-3 md:p-4 text-center">
                  <div className="text-xl md:text-3xl font-black text-teal-400">99.9%</div>
                  <div className="text-[10px] md:text-xs text-white/70 mt-1 font-medium">측정 정확도</div>
                </div>
                <div className="bg-white/10 rounded-xl p-3 md:p-4 text-center">
                  <div className="text-xl md:text-3xl font-black text-teal-400">0%</div>
                  <div className="text-[10px] md:text-xs text-white/70 mt-1 font-medium">납 성분</div>
                </div>
                <div className="bg-white/10 rounded-xl p-3 md:p-4 text-center">
                  <div className="text-xl md:text-3xl font-black text-teal-400">24/7</div>
                  <div className="text-[10px] md:text-xs text-white/70 mt-1 font-medium">실시간 모니터링</div>
                </div>
                <div className="bg-white/10 rounded-xl p-3 md:p-4 text-center">
                  <div className="text-xl md:text-3xl font-black text-teal-400">100%</div>
                  <div className="text-[10px] md:text-xs text-white/70 mt-1 font-medium">부정사용 차단</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 mb-10 md:mb-16">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group relative bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-teal-300 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 p-5 md:p-8"
            >
              {/* Icon Badge */}
              <div
                className={`w-12 md:w-16 h-12 md:h-16 bg-gradient-to-br ${feature.gradient} rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}
              >
                <i className={`${feature.icon} text-2xl md:text-3xl text-white`}></i>
              </div>

              {/* Stats Badge */}
              <div className="absolute top-4 md:top-6 right-4 md:right-6 bg-teal-50 px-2 md:px-3 py-1 md:py-1.5 rounded-lg">
                <div className="text-base md:text-xl font-black text-teal-600">
                  {feature.stats.value}
                </div>
                <div className="text-[8px] md:text-[10px] text-gray-500 font-semibold">
                  {feature.stats.label}
                </div>
              </div>

              {/* Content */}
              <h3 className="text-base md:text-xl font-bold text-gray-900 mb-2 md:mb-3 group-hover:text-teal-600 transition-colors duration-300 pr-16 md:pr-20">
                {feature.title}
              </h3>
              <p className="text-xs md:text-sm text-gray-600 leading-relaxed font-medium">
                {feature.description}
              </p>

              {/* Bottom Accent Line */}
              <div
                className={`mt-4 md:mt-6 h-1 w-0 bg-gradient-to-r ${feature.gradient} group-hover:w-full transition-all duration-500 rounded-full`}
              ></div>

              {/* Hover Glow */}
              <div
                className={`absolute -bottom-20 -right-20 w-40 h-40 bg-gradient-to-br ${feature.gradient} rounded-full blur-3xl opacity-0 group-hover:opacity-10 transition-all duration-500`}
              ></div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <a
            href="#contact"
            className="inline-block px-8 md:px-12 py-4 md:py-5 bg-gradient-to-r from-teal-600 to-cyan-600 text-white text-base md:text-lg font-bold rounded-full hover:shadow-2xl transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            제품 문의하기
          </a>
        </div>
      </div>
    </section>
  );
}
